import { useState } from 'react'
import { Linking, Platform, Pressable, StyleSheet, Text, View } from 'react-native'
import { colors, fontWeight, radius } from '../tokens'
import { trpc } from '../trpc'
import { ErrorNotice } from './ErrorNotice'
import { FulfilmentEmailPreview } from './FulfilmentEmailPreview'
import { FulfilmentReminders } from './FulfilmentReminders'
import { FulfilmentSent } from './FulfilmentSent'
import { FulfilmentStep } from './FulfilmentStep'

type Props = {
  appealId: string
  evidenceUrl: string
  bandReviewEmail: { to: string; subject: string; body: string; mailto: string }
  walkthroughToolUrl: string
  scotlandGuideUrl?: string | null
  onCheckAnother: () => void
}

const openLink = (url: string) => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }
  void Linking.openURL(url)
}

// Stepper that locks the user into download → email → confirm in order.
// The attachment chip in the email preview flips green once step 1 is done
// so they can see the dependency live; the confirm button stays disabled
// until both prior steps are complete to prevent a premature "sent" mark.
export const FulfilmentStepper = ({
  appealId,
  evidenceUrl,
  bandReviewEmail,
  walkthroughToolUrl,
  scotlandGuideUrl,
  onCheckAnother,
}: Props) => {
  const [downloaded, setDownloaded] = useState(false)
  const [emailed, setEmailed] = useState(false)
  const [copied, setCopied] = useState(false)
  const recordChoice = trpc.appeal.recordPathChoice.useMutation()
  const recordStep = trpc.appeal.recordFulfilmentStep.useMutation()
  const markSent = trpc.appeal.markEmailSent.useMutation()
  const emailPack = trpc.appeal.emailPack.useMutation()
  const canConfirm = downloaded && emailed && !markSent.isPending

  if (markSent.isSuccess) {
    return <FulfilmentSent onCheckAnother={onCheckAnother} />
  }

  // Don't record submissionPath on download — download alone isn't a
  // commitment to the email route; step 2 (clicking the mailto button) is
  // the stronger signal. recordPathChoice's "stronger-commitment-wins"
  // policy (see appeal-router.ts) prefers we only fire on the explicit
  // choice.
  const onDownloaded = () => {
    setDownloaded(true)
    // Funnel step 3 — fire-and-forget telemetry; never block the download.
    recordStep.mutate({ appealId, step: 'downloaded' })
  }

  const onEmail = () => {
    openLink(bandReviewEmail.mailto)
    recordChoice.mutate({ appealId, path: 'BAND_REVIEW_EMAIL' })
    recordStep.mutate({ appealId, step: 'email_opened' })
    setEmailed(true)
  }

  const onCopyBody = async () => {
    if (Platform.OS !== 'web' || typeof navigator === 'undefined' || !navigator.clipboard) return
    try {
      await navigator.clipboard.writeText(bandReviewEmail.body)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      // Clipboard API can fail (permissions / non-secure context). Silent.
    }
  }

  const onWalkthrough = () => {
    openLink(walkthroughToolUrl)
    recordChoice.mutate({ appealId, path: 'BAND_REVIEW_ONLINE' })
  }

  return (
    <View style={styles.card}>
      <FulfilmentStep
        number={1}
        done={downloaded}
        title="Download your evidence appendix"
        description={
          <Text style={styles.desc}>
            A PDF with your{' '}
            <Text style={styles.descBold}>comparable homes, their sale prices and floor areas</Text>{' '}
            — the proof the Valuation Office needs to see. You'll attach it to the email in step 2.
          </Text>
        }
      >
        <DownloadButton
          done={downloaded}
          url={evidenceUrl}
          filename="evidence-appendix.pdf"
          label={downloaded ? 'Appendix downloaded' : 'Download appendix (PDF)'}
          onDownloaded={onDownloaded}
          testID="fulfilment-step1-download"
        />
        {downloaded ? (
          <Text style={styles.hintDone}>✓ Saved. Keep it handy — you'll attach it next.</Text>
        ) : null}
        {downloaded && Platform.OS === 'web' ? (
          <EmailFallback appealId={appealId} emailPack={emailPack} />
        ) : null}
        {scotlandGuideUrl && Platform.OS === 'web' ? (
          <a
            data-testid="scotland-guide-download"
            href={scotlandGuideUrl}
            download="scotland-guide.pdf"
            style={{
              color: colors.primary[700],
              fontWeight: 600,
              textDecoration: 'none',
              fontSize: 13,
              marginTop: 12,
              display: 'inline-block',
            }}
          >
            ↓ Scotland: find your local Assessor (guide)
          </a>
        ) : null}
      </FulfilmentStep>

      <FulfilmentStep
        number={2}
        done={emailed}
        title="Email the Valuation Office — with the appendix attached"
        description={
          <Text style={styles.desc}>
            We'll open an email with your{' '}
            <Text style={styles.descBold}>Band Review message already written</Text>. Attach the PDF
            from step 1, then hit send.
          </Text>
        }
      >
        <FulfilmentEmailPreview
          to={bandReviewEmail.to}
          subject={bandReviewEmail.subject}
          attachmentReady={downloaded}
        />
        <StepButton
          done={emailed}
          label={emailed ? 'Email opened' : 'Open the pre-written email'}
          onPress={onEmail}
          testID="fulfilment-step2-email"
        />
        <View style={styles.alt}>
          <Text style={styles.altLead}>Prefer to do it yourself?</Text>
          {Platform.OS === 'web' ? (
            <Text style={styles.altLink} accessibilityRole="link" onPress={onCopyBody}>
              {copied ? 'Copied!' : 'Copy the email text'}
            </Text>
          ) : null}
          <Text style={styles.altSep}>·</Text>
          <Text style={styles.altLink} accessibilityRole="link" onPress={onWalkthrough}>
            Use the gov.uk online tool
          </Text>
        </View>
      </FulfilmentStep>

      <FulfilmentStep
        number={3}
        done={false}
        isLast
        title="Tell us once it's sent"
        description={
          <Text style={styles.desc}>
            We'll watch the Valuation Office's response window and remind you to follow up at the
            right moments — so you never have to chase it yourself.
          </Text>
        }
      >
        <FulfilmentReminders active={0} />
        <View style={{ height: 18 }} />
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ disabled: !canConfirm }}
          disabled={!canConfirm}
          onPress={() => markSent.mutate({ appealId })}
          style={[styles.confirm, !canConfirm && styles.confirmDisabled]}
          testID="fulfilment-step3-confirm"
        >
          <Text style={[styles.confirmText, !canConfirm && styles.confirmTextDisabled]}>
            {markSent.isPending ? 'Saving…' : "✓ I've sent it to the Valuation Office"}
          </Text>
        </Pressable>
        {!canConfirm && !markSent.isPending ? (
          <Text style={styles.hintCentered}>Complete steps 1 and 2 first</Text>
        ) : null}
        <ErrorNotice error={markSent.error} />
      </FulfilmentStep>
    </View>
  )
}

const StepButton = ({
  done,
  label,
  onPress,
  testID,
}: {
  done: boolean
  label: string
  onPress: () => void
  testID?: string
}) => (
  <Pressable
    accessibilityRole="button"
    onPress={onPress}
    style={[styles.stepBtn, done && styles.stepBtnDone]}
    testID={testID}
  >
    <Text style={[styles.stepBtnText, done && styles.stepBtnTextDone]}>
      {done ? '✓ ' : '↓ '}
      {label}
    </Text>
  </Pressable>
)

// Shown below step 1 after first download attempt. Lets the user fall back
// to an emailed link if the browser download didn't work.
type EmailFallbackProps = {
  appealId: string
  emailPack: ReturnType<typeof trpc.appeal.emailPack.useMutation>
}
const EmailFallback = ({ appealId, emailPack }: EmailFallbackProps) => {
  if (emailPack.isSuccess) {
    return <Text style={styles.emailFallbackSent}>✓ Sent — check your inbox.</Text>
  }
  return (
    <Pressable
      onPress={() => emailPack.mutate({ appealId })}
      disabled={emailPack.isPending}
      accessibilityRole="button"
    >
      <Text style={styles.emailFallbackLink}>
        {emailPack.isPending ? 'Sending…' : "Didn't download? Email it to me instead"}
      </Text>
    </Pressable>
  )
}

// Anchor-based variant styled to look like StepButton. We need a real
// <a download> on web so the click triggers a file download — using
// window.open / Linking.openURL navigates instead, and the user is taken
// away from our app right before step 2 (which needs them to come back).
// Server sends Content-Disposition: attachment too, so even cross-origin
// browsers honour the download instead of inline-rendering.
const DownloadButton = ({
  done,
  url,
  filename,
  label,
  onDownloaded,
  testID,
}: {
  done: boolean
  url: string
  filename: string
  label: string
  onDownloaded: () => void
  testID?: string
}) => {
  if (Platform.OS !== 'web') {
    return (
      <Pressable
        accessibilityRole="button"
        onPress={() => {
          void Linking.openURL(url)
          onDownloaded()
        }}
        style={[styles.stepBtn, done && styles.stepBtnDone]}
        testID={testID}
      >
        <Text style={[styles.stepBtnText, done && styles.stepBtnTextDone]}>
          {done ? '✓ ' : '↓ '}
          {label}
        </Text>
      </Pressable>
    )
  }
  return (
    <a
      data-testid={testID}
      href={url}
      download={filename}
      onClick={() => {
        // Defer state update so the browser initiates the download before
        // React re-renders. Re-clicking when already done is fine — the
        // download restarts and setDownloaded(true) is idempotent.
        setTimeout(onDownloaded, 0)
      }}
      style={{
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 50,
        paddingLeft: 22,
        paddingRight: 22,
        borderRadius: 12,
        textDecoration: 'none',
        cursor: done ? 'default' : 'pointer',
        fontSize: 15.5,
        fontWeight: 600,
        backgroundColor: done ? colors.positiveSoft : colors.primary[700],
        color: done ? colors.positiveInk : '#fff',
      }}
    >
      {done ? '✓ ' : '↓ '}
      {label}
    </a>
  )
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: radius.lg,
    paddingHorizontal: 30,
    paddingVertical: 8,
  },
  desc: { fontSize: 14.5, color: colors.muted, lineHeight: 23 },
  descBold: { color: colors.ink, fontWeight: fontWeight.semibold },
  hintDone: {
    marginTop: 12,
    fontSize: 13,
    color: colors.positiveInk,
  },
  emailFallbackLink: {
    marginTop: 10,
    fontSize: 13,
    color: colors.primary[700],
    textDecorationLine: 'underline',
  },
  emailFallbackSent: {
    marginTop: 10,
    fontSize: 13,
    color: colors.positiveInk,
  },
  hintCentered: {
    marginTop: 12,
    fontSize: 13,
    color: colors.muted,
    textAlign: 'center',
  },
  stepBtn: {
    alignSelf: 'flex-start',
    height: 50,
    paddingHorizontal: 22,
    borderRadius: 12,
    backgroundColor: colors.primary[700],
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepBtnDone: { backgroundColor: colors.positiveSoft },
  stepBtnText: { color: '#fff', fontSize: 15.5, fontWeight: fontWeight.semibold },
  stepBtnTextDone: { color: colors.positiveInk },
  alt: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 14 },
  altLead: { fontSize: 13, color: colors.muted },
  altLink: { fontSize: 13, color: colors.primary[700], fontWeight: fontWeight.semibold },
  altSep: { fontSize: 13, color: colors.faint },
  confirm: {
    height: 54,
    borderRadius: 12,
    backgroundColor: colors.positive,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  confirmDisabled: { backgroundColor: colors.surface2 },
  confirmText: { color: '#fff', fontSize: 16, fontWeight: fontWeight.semibold },
  confirmTextDisabled: { color: colors.faint },
  error: { color: colors.error[700], fontSize: 13, marginTop: 8, textAlign: 'center' },
})
